import { Grid, Paper, styled } from '@mui/material';
import axios, { AxiosResponse } from 'axios';
import { FormProvider, useForm } from 'react-hook-form';
import { useMutation } from '@tanstack/react-query';
import { API_URL, LANGUAGES, VOICES } from '../constants';
import { Message } from '../types';
import uuid from '../uuid';
import Filters from './Filters';
import Messages from './Messages';
import WidgetInput from './WidgetInput';
import useToast from '../hooks/useToast';
import { Buffer } from 'buffer';
import { useState } from 'react';
import { ReadableStreamDefaultReadResult } from 'stream/web';

const WidgetContainer = styled(Paper)(({ theme }) => ({
	padding: theme.spacing(4),
}));

export interface FormValues {
	language: string;
	voice: string;
	text: string;
	messages: Message[];
}

interface MutationProps {
	speaker: string;
	text: string;
	language_code: string;
}

export default function Widget() {
	const toast = useToast();

	const formMethods = useForm<FormValues>({
		defaultValues: {
			language: LANGUAGES[0].code,
			voice: VOICES[0].code,
		},
	});

	const [messages, setMessages] = useState<Message[]>([
		{
			id: uuid(),
			senderName: 'DayusKIT',
			text: 'Пожалуйста, введите текст, который вы хотите синтезировать в поле ниже',
		},
	]);

	const sendMessageMutation = useMutation(
		['sendMessage'],
		(data: MutationProps) =>
			fetch(API_URL, {
				method: 'POST',
				body: JSON.stringify(data),
				headers: {
					'Content-Type': 'application/json',
				},
			})
				.then((response: Response) => {
					if (!response.body) {
						return '';
					}
					const reader = response.body.getReader();

					return new ReadableStream({
						start(controller) {
							return pump();

							function pump(): Promise<
								ReadableStreamDefaultReadResult<any> | undefined
							> {
								return reader.read().then(({ done, value }) => {
									// When no more data needs to be consumed, close the stream
									if (done) {
										controller.close();
										return;
									}
									// Enqueue the next data chunk into our target stream
									controller.enqueue(value);
									return pump();
								});
							}
						},
					});
				})
				// Create a new response out of the stream
				.then((stream) => new Response(stream))
				// Create an object URL for the response
				.then((response) => response.blob())
				.then((blob) => URL.createObjectURL(blob)),
		{
			onError(error, variables, context) {
				console.log(error);
				toast('Произошла непредвиденная ошибка. Попробуйте еще раз');
			},
			onSuccess(data, variables, context) {
				const values = formMethods.getValues();
				formMethods.resetField('text');
				setMessages((prev) => [
					...prev,
					{
						assetURL: data,
						text: values.text,
						isRight: true,
						senderName: VOICES.find((el) => el.code === values.voice)?.label,
						id: uuid(),
					},
					{
						id: uuid(),
						senderName: 'DayusKIT',
						text: 'Попробуем еще раз?',
					},
				]);
			},
		}
	);

	const { isLoading } = sendMessageMutation;

	const { handleSubmit } = formMethods;

	const onSubmit = (data: FormValues) => {
		if (isLoading) {
			return;
		}

		sendMessageMutation.mutate({
			language_code: data.language,
			speaker: data.voice,
			text: data.text,
		});
	};

	return (
		<WidgetContainer elevation={0}>
			<FormProvider {...formMethods}>
				<Grid
					container
					spacing={4}
					component="form"
					onSubmit={handleSubmit(onSubmit)}
				>
					<Grid item xs={12} sm display="flex" flexDirection="column">
						<Messages messages={messages} isLoading={isLoading} />
						<WidgetInput isLoading={isLoading} />
					</Grid>
					<Grid item xs={12} md={4} lg={3}>
						<Filters isLoading={isLoading} />
					</Grid>
				</Grid>
			</FormProvider>
		</WidgetContainer>
	);
}
